import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { Table, DatePicker, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  fetchMicroQueueData,
} from "../../../../actions/micro";
import useAbortController from "../../../Common/hooks/useAbortController";
import Legend from "../../../Common/Legend/Legend";
import { TEST_OVERDUE_FLAGS, convertStatusMapToArrayOptions, getTestOverdueFlag } from "../../../../utils/statusConfig";

import "./MicroQueue.css";

const LegendContentArray = convertStatusMapToArrayOptions(TEST_OVERDUE_FLAGS, [], true, false);

const MicroQueueTableRow = ({
  record,
  ...antdProps
}) => {
  if (!record) {
    return <tr {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }

  return (
    <>
      <tr
        {...antdProps} // eslint-disable-line
        className="MicroQueueTableRow"
      />
    </>
  );
};

const MicroQueueTableCell = ({
  record,
  dataIndex,
  ...antdProps
}) => {
  const tooltipTitle = useCallback((element) => {
    const baseInfo = [
      { label: "Work order", param: "order_id" },
      { label: "Assigned To", param: "assigned_to" },
      { label: "Sample ID", param: "sample_id" },
      { label: "Sample Type", param: "sample_type" },
      { label: "QC Status", param: "qc_status" },
    ];
    const validInfo = baseInfo.filter((item) => element[item.param]); // Only render a HTML dom if the value is valid

    return (
      <div className="PendingTable__Tooltip__Container">
        {validInfo.map((item, i) => {
          const { label, param: keyParam } = item;
          const value = element[keyParam];
          const childNode = (<span>{value}</span>);
          return (
            <div className="PendingTable__Tooltip__Content" key={i}>
              <label>{`${label}:`}</label>
              {childNode}
            </div>
          );
        })}
      </div>
    );
  }, []);
  if (!record) {
    return <td {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }
  let childNode = record?.[dataIndex] || "-";
  if (dataIndex === "id") {
    childNode = (record[dataIndex].map((item, index) => {
      const { genista_id, overdue_by, status } = item;
      let overdueFlagStyle = TEST_OVERDUE_FLAGS[getTestOverdueFlag(overdue_by)]?.textStyle;
      if (status === "Retest") {
        overdueFlagStyle = { ...overdueFlagStyle, ...TEST_OVERDUE_FLAGS.Retest.textStyle };
      }
      return (
        <Tooltip
          key={`genista_tooltip_${index}`}
          placement="top"
          title={tooltipTitle(item)}
          overlayClassName="PendingTable__Tooltip"
          overlayStyle={{ minWidth: "350px" }}
        >
          <span
            className="GenIdCell"
            style={overdueFlagStyle}
          >
            {index + 1
          === record[dataIndex].length
              ? genista_id
              : `${genista_id}, `}
          </span>
        </Tooltip>
      );
    }));
  }
  return <td {...antdProps}>{childNode}</td>; // eslint-disable-line react/jsx-props-no-spreading
};

function MicroPending() {
  const columns = [
    {
      title: "Test",
      dataIndex: "Test",
      key: "Test",
      permission: "no-permission",
      width: 200,
    },
    {
      title: "Test Category",
      dataIndex: "test_category_name",
      key: "test_category_name",
      permission: "no-permission",
      width: 180,
    },
    {
      title: "Genista ID",
      dataIndex: "id",
      key: "id",
      permission: "no-permission",
    },
  ];
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [uniqueQueueTestCategories, setUniqueQueueTestCategories] = useState([]);
  const today = dayjs().format("MM/DD/YY");
  const { setNewAbortController } = useAbortController();

  const handleFetchMicroQueue = async (date = today) => {
    setLoading(true);
    const newAbortController = setNewAbortController();
    const {
      success, message, List, test_categories, canceled,
    } = await fetchMicroQueueData({ date }, newAbortController.signal);
    if (success) {
      setData(List.map((item) => ({ ...item, key: uuidv4() })));
      setUniqueQueueTestCategories(test_categories);
    } else if (!canceled) {
      toast.error(message);
      setData([]);
      setUniqueQueueTestCategories([]);
    }
    setLoading(canceled);
  };

  useEffect(() => {
    handleFetchMicroQueue();
  }, []); // eslint-disable-line

  const handleDate = (e) => {
    const date = dayjs(e.$d).format("MM/DD/YY");
    handleFetchMicroQueue(date);
  };

  return (
    <div className="MicroQueue">
      <div className="MicroQueue__FilterContainer">
        <h5>
          Pending/Queue Data
          {uniqueQueueTestCategories.length > 0 ? ` for ${uniqueQueueTestCategories.join(", ")}` : ""}
        </h5>
        <div className="dropdownSHFTContainer">
          <DatePicker
            allowClear={false}
            format="MM/DD/YYYY"
            defaultValue={dayjs(today)}
            onChange={(e) => handleDate(e)}
            className="Dashboard__Activity__DatePicker"
          />
        </div>
        <Legend legendContent={LegendContentArray} />
      </div>
      <Table
        sticky
        className="MicroQueueTable"
        dataSource={data}
        loading={loading}
        pagination={false}
        components={{
          body: {
            row: MicroQueueTableRow,
            cell: MicroQueueTableCell,
          },
        }}
        onRow={(record) => ({ record })}
        columns={columns.map((col) => ({
          ...col,
          onCell: (record) => ({
            record,
            dataIndex: col.dataIndex,
          }),
        }))}
      />
    </div>
  );
}

export default MicroPending;
